import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../api/api';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

export const productApi = createApi({
  reducerPath: 'productApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Product', 'Category', 'Size', 'Color'],
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: (page = 1) => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/product',
          method: 'GET',
          params: { page, 'access-token': token },
        };
      },
      transformResponse: (response) => {
        // Transform API response to match component expectations
        if (response?.status === 1 && response?.data?.[0]?.items) {
          const items = response.data[0].items;
          const meta = response.data[0]._meta;
          
          // Transform each product to match component format while preserving original API structure
          const transformedItems = items.map((product) => ({
            // Preserve original API structure for edit/view
            ...product,
            // Add transformed fields for display
            nameEn: product.name_en,
            descAr: product.text,
            descEn: product.text_en,
            discountPrice: product.dis_price,
            calories: product.calorie,
            active: product.status === 1,
            statusName: product.status_name,
            offers: product.offer?.toString() || "0",
            new: product.new?.toString() || "0",
            mostSell: product.best_seller?.toString() || "0",
            departmentTitle: product.category_id?.name || '',
            departmentId: product.category_id?.id || '',
            departmentImg: product.category_id?.image || '',
            productImages: product.productImages || [],
            productColor: product.product_color || [],
            productSize: product.product_size || [],
            hasSizes: product.product_size?.length > 0,
            hasColors: product.product_color?.length > 0,
          }));
          
          return {
            items: transformedItems,
            meta: {
              totalCount: meta?.totalCount || 0,
              currentPage: meta?.currentPage || 1,
              perPage: meta?.perPage || 12,
              totalPages: meta?.NumberOfPage || 1,
            }
          };
        }
        return { items: [], meta: { totalCount: 0, currentPage: 1, perPage: 12, totalPages: 1 } };
      },
      providesTags: ['Product'],
    }),
    getCategories: builder.query({
      query: () => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/category',
          method: 'GET',
          params: { 'access-token': token },
        };
      },
      providesTags: ['Category'],
    }),
    // New endpoint for sizes
    getSizes: builder.query({
      query: () => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/size',
          method: 'GET',
          params: { 'access-token': token },
        };
      },
      transformResponse: (response) => {
        // Transform API response to match component expectations
        if (response?.status === 1 && response?.data?.[0]?.items) {
          return {
            data: response.data,
          };
        }
        return { data: { items: [] } };
      },
      providesTags: ['Size'],
    }),
    // New endpoint for colors
    getColors: builder.query({
      query: () => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/color',
          method: 'GET',
          params: { 'access-token': token },
        };
      },
      transformResponse: (response) => {
        // Transform API response to match component expectations
        if (response?.status === 1 && response?.data?.[0]?.items) {
          return {
            data: response.data,
          };
        }
        return { data: { items: [] } };
      },
      providesTags: ['Color'],
    }),
    addProduct: builder.mutation({
      query: (formData) => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/add-product',
          method: 'POST',
          data: formData,
          params: { 'access-token': token },
          isFormData: true,
        };
      },
      invalidatesTags: ['Product'],
    }),
    updateProduct: builder.mutation({
      query: (formData) => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/update-product',
          method: 'POST',
          data: formData,
          params: { 'access-token': token },
          isFormData: true,
        };
      },
      invalidatesTags: ['Product'],
    }),
    deleteProduct: builder.mutation({
      query: (formData) => {
        const token = cookies.get('authToken');
        return {
          url: 'api/web/v1/site/delete-product',
          method: 'POST',
          data: formData,
          params: { 'access-token': token },
          isFormData: true,
        };
      },
      invalidatesTags: ['Product'],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetCategoriesQuery,
  useGetSizesQuery,
  useGetColorsQuery,
  useAddProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} = productApi;